import { z } from 'zod';
import { idSchema } from './common.js';
/**
 * Eating profiles, meal places and planner vocabularies (D31–D33). Keys are
 * stored; labels are for display. `packages/core` keeps its own copies of
 * ALLERGENS, DIETS and VIBES (it has no dependencies); `profiles-sync.test.ts`
 * fails if they drift.
 */
export const ALLERGENS = [
    'peanut',
    'tree_nut',
    'dairy',
    'egg',
    'gluten',
    'soy',
    'shellfish',
    'fish',
    'sesame',
];
export const allergenSchema = z.enum(ALLERGENS);
export const ALLERGEN_LABELS = {
    peanut: 'Peanuts',
    tree_nut: 'Tree nuts',
    dairy: 'Dairy',
    egg: 'Eggs',
    gluten: 'Gluten',
    soy: 'Soy',
    shellfish: 'Shellfish',
    fish: 'Fish',
    sesame: 'Sesame',
};
export const DIETS = [
    'vegetarian',
    'vegan',
    'pescatarian',
    'halal',
    'kosher',
    'low_carb',
    'low_sodium',
    'diabetic_friendly',
];
export const dietSchema = z.enum(DIETS);
export const DIET_LABELS = {
    vegetarian: 'Vegetarian',
    vegan: 'Vegan',
    pescatarian: 'Pescatarian',
    halal: 'Halal',
    kosher: 'Kosher',
    low_carb: 'Low carb',
    low_sodium: 'Low sodium',
    diabetic_friendly: 'Diabetic friendly',
};
export const VIBES = [
    'quick_easy',
    'one_pan',
    'batch_cook',
    'family_favorites',
    'comfort_classics',
    'fresh_light',
    'fiber_forward',
    'takeout_at_home',
];
export const vibeSchema = z.enum(VIBES);
export const VIBE_LABELS = {
    quick_easy: 'Quick & easy',
    one_pan: 'One pan',
    batch_cook: 'Batch cook',
    family_favorites: 'Family favorites',
    comfort_classics: 'Comfort classics',
    fresh_light: 'Fresh & light',
    fiber_forward: 'Fiber forward',
    takeout_at_home: 'Takeout at home',
};
/** A planner request's vibe: one of `VIBES`, or `any` (no vibe term). */
export const plannerVibeSchema = z.union([vibeSchema, z.literal('any')]);
/** What a meal place offers for packed meals. */
export const APPLIANCES = ['microwave', 'fridge', 'toaster_oven', 'kettle'];
export const applianceSchema = z.enum(APPLIANCES);
export const APPLIANCE_LABELS = {
    microwave: 'Microwave',
    fridge: 'Fridge',
    toaster_oven: 'Toaster oven',
    kettle: 'Kettle',
};
export const PLACE_KINDS = ['home', 'work', 'school', 'other'];
export const placeKindSchema = z.enum(PLACE_KINDS);
export const MEAL_PLAN_KINDS = ['cook', 'leftovers', 'dine_out', 'packed'];
export const mealPlanKindSchema = z.enum(MEAL_PLAN_KINDS);
export const PREFERENCE_KINDS = ['like', 'dislike'];
export const preferenceKindSchema = z.enum(PREFERENCE_KINDS);
export const PREFERENCE_SOURCES = ['stated', 'learned'];
export const preferenceSourceSchema = z.enum(PREFERENCE_SOURCES);
const uniqueList = (item, max) => z
    .array(item)
    .max(max)
    .transform((xs) => [...new Set(xs)]);
// ------------------------------------------------------ person profile (D31) --
/** PATCH /api/v1/households/me/people/:id/profile — every field optional. */
export const personProfileRequestSchema = z.object({
    allergens: uniqueList(allergenSchema, ALLERGENS.length).optional(),
    diets: uniqueList(dietSchema, DIETS.length).optional(),
    notes: z.string().trim().max(400).nullable().optional(),
});
export const personPreferenceDtoSchema = z.object({
    ingredientId: idSchema,
    ingredientName: z.string(),
    kind: preferenceKindSchema,
    source: preferenceSourceSchema,
    /** (0, 1]; stated entries are always 1. */
    strength: z.number(),
    updatedAt: z.iso.datetime(),
});
/** GET/PUT /api/v1/households/me/people/:id/preferences */
export const personPreferencesResponseSchema = z.object({
    personId: idSchema,
    /** Stated likes/dislikes first, then learned ones ("We think Emma likes…"). */
    preferences: z.array(personPreferenceDtoSchema),
});
/**
 * PUT /api/v1/households/me/people/:id/preferences. `likes`/`dislikes`
 * replace the person's *stated* preferences (omit a list to leave it alone);
 * `confirm` turns learned entries into stated ones (same kind); `reject`
 * deletes learned entries ("not really"). An ingredient in both `likes` and
 * `dislikes` is a 400.
 */
export const putPersonPreferencesRequestSchema = z
    .object({
    likes: uniqueList(idSchema, 200).optional(),
    dislikes: uniqueList(idSchema, 200).optional(),
    confirm: uniqueList(idSchema, 200).optional(),
    reject: uniqueList(idSchema, 200).optional(),
})
    .refine((b) => !(b.likes ?? []).some((id) => (b.dislikes ?? []).includes(id)), 'An ingredient cannot be both liked and disliked');
// ------------------------------------------------------- meal places (D32) --
export const mealPlaceDtoSchema = z.object({
    id: idSchema,
    name: z.string(),
    kind: placeKindSchema,
    appliances: z.array(applianceSchema),
    bannedAllergens: z.array(allergenSchema),
    notes: z.string().nullable(),
    sort: z.number().int(),
});
/** GET /api/v1/households/me/meal-places — always contains a `home` row. */
export const mealPlacesResponseSchema = z.object({ places: z.array(mealPlaceDtoSchema) });
/** POST /api/v1/households/me/meal-places */
export const createMealPlaceRequestSchema = z.object({
    name: z.string().trim().min(1).max(80),
    kind: placeKindSchema.default('other'),
    appliances: uniqueList(applianceSchema, APPLIANCES.length).default([]),
    bannedAllergens: uniqueList(allergenSchema, ALLERGENS.length).default([]),
    notes: z.string().trim().max(400).nullable().optional(),
    sort: z.number().int().min(0).max(1000).optional(),
});
/** PATCH /api/v1/households/me/meal-places/:id */
export const updateMealPlaceRequestSchema = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    kind: placeKindSchema.optional(),
    appliances: uniqueList(applianceSchema, APPLIANCES.length).optional(),
    bannedAllergens: uniqueList(allergenSchema, ALLERGENS.length).optional(),
    notes: z.string().trim().max(400).nullable().optional(),
    sort: z.number().int().min(0).max(1000).optional(),
});
// ------------------------------------------------------ plan attendees (D32) --
/** A meal's eater: a household person, or a named guest (`personId` null). */
export const planAttendeeDtoSchema = z.object({
    personId: idSchema.nullable(),
    /** Person's current name, or the guest's name. */
    name: z.string(),
    guest: z.boolean(),
});
export const planAttendeesInputSchema = z.object({
    personIds: uniqueList(idSchema, 50).default([]),
    guests: z.array(z.string().trim().min(1).max(80)).max(30).default([]),
});
/** PUT /api/v1/plans/:id/attendees — replaces the list; empty = everyone. */
export const putPlanAttendeesRequestSchema = planAttendeesInputSchema.extend({
    /** Also set servings to the attendee count (default true). */
    syncServings: z.boolean().default(true),
});
/**
 * Computed on read for a plan's attendees (everyone when it has none):
 * `dislike` ("Emma dislikes mushrooms"), `allergen` (a person's allergen is
 * in a recipe — shown as a "contains" chip), `place_allergen` (banned at the
 * meal's place) and `diet` (a recipe conflicts with, or cannot be checked
 * for, an attendee's diet).
 */
export const planWarningSchema = z.object({
    kind: z.enum(['dislike', 'allergen', 'place_allergen', 'diet']),
    text: z.string(),
    personId: idSchema.nullable().optional(),
    recipeId: idSchema.nullable().optional(),
    ingredientId: idSchema.nullable().optional(),
    allergen: allergenSchema.optional(),
    diet: dietSchema.optional(),
});
//# sourceMappingURL=profiles.js.map